import { useCallback } from "react";
import { rewardsErrorMessageKey } from "@ticketon/rewards-graphql";
import { useRewardsAdmin } from "../host/RewardsAdminHost";

export interface IUseMutationFeedback {
  /** Toasts a translated success message. */
  notifySuccess: (messageKey: string) => void;
  /** Toasts the translated rewards error (`t(rewardsErrorMessageKey(error))`). */
  notifyError: (error: unknown) => void;
  /**
   * Runs a mutation, toasting success or the translated error. Never throws:
   * resolves with the data, or `undefined` when the mutation failed.
   */
  runMutation: <TData>(operation: () => Promise<TData>, successKey?: string) => Promise<TData | undefined>;
}

/** Success / error toasts for rewards mutations through the host's `notify`. */
export function useMutationFeedback(): IUseMutationFeedback {
  const { t, notify } = useRewardsAdmin();

  const notifySuccess = useCallback((messageKey: string) => notify(t(messageKey), "success"), [notify, t]);
  const notifyError = useCallback(
    (error: unknown) => notify(t(rewardsErrorMessageKey(error)), "error"),
    [notify, t],
  );

  const runMutation = useCallback(
    async <TData,>(operation: () => Promise<TData>, successKey?: string): Promise<TData | undefined> => {
      try {
        const data = await operation();
        if (successKey) notifySuccess(successKey);
        return data;
      } catch (error) {
        notifyError(error);
        return undefined;
      }
    },
    [notifyError, notifySuccess],
  );

  return { notifySuccess, notifyError, runMutation };
}
